import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { getAuthToken } from '../utils/authToken';

const PERIOD_OPTIONS = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: 'all', label: 'All Available Logs' },
    { value: 'custom', label: 'Custom Range' },
];

const LEVEL_COLOR_MAP = {
    ERROR: 'error',
    WARN: 'warning',
    WARNING: 'warning',
    INFO: 'info',
    DEBUG: 'default',
    TRACE: 'default',
    CRITICAL: 'error',
    FATAL: 'error',
};

function formatBytes(bytes) {
    if (typeof bytes !== 'number' || Number.isNaN(bytes)) return '0 B';
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, exponent);
    return `${value.toFixed(value >= 10 || value % 1 === 0 ? 0 : 1)} ${units[exponent]}`;
}

function formatTimestamp(timestamp) {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return timestamp;
    return date.toLocaleString();
}

function levelToChipProps(level) {
    if (!level) {
        return { label: '—', variant: 'outlined', size: 'small' };
    }
    const normalized = level.toUpperCase();
    const color = LEVEL_COLOR_MAP[normalized] || 'default';
    return {
        label: normalized,
        color,
        variant: color === 'default' ? 'outlined' : 'filled',
        size: 'small',
    };
}

function AdminSystemLogsPage() {
    const tokenFromState = useSelector((state) => state.auth.token);
    const token = getAuthToken(tokenFromState);

    const [period, setPeriod] = useState('24h');
    const [customRange, setCustomRange] = useState({ start: '', end: '' });
    const [entries, setEntries] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);

    const canRequest = useMemo(() => {
        if (period !== 'custom') return true;
        return Boolean(customRange.start && customRange.end);
    }, [period, customRange.start, customRange.end]);

    const buildQueryString = useCallback(() => {
        const params = new URLSearchParams({ period });
        if (period === 'custom' && customRange.start && customRange.end) {
            params.set('start', new Date(customRange.start).toISOString());
            params.set('end', new Date(customRange.end).toISOString());
        }
        return params.toString();
    }, [period, customRange.start, customRange.end]);

    const fetchLogs = useCallback(() => {
        if (!token || !canRequest) return;
        setLoading(true);
        setError('');
        axios
            .get(`/api/admin/system-logs?${buildQueryString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                setEntries(res.data?.entries || []);
                setFiles(res.data?.files || []);
                setLastUpdated(new Date().toISOString());
            })
            .catch((err) => {
                const message = err.response?.data?.msg || 'Failed to load system logs.';
                setError(message);
            })
            .finally(() => setLoading(false));
    }, [token, canRequest, buildQueryString]);

    useEffect(() => {
        if (!token || !canRequest) return;
        fetchLogs();
    }, [token, canRequest, fetchLogs]);

    const handlePeriodChange = (event) => {
        const { value } = event.target;
        setPeriod(value);
        setError('');
        if (value !== 'custom') {
            setCustomRange({ start: '', end: '' });
        }
    };

    const handleCustomChange = (key) => (event) => {
        setCustomRange((prev) => ({ ...prev, [key]: event.target.value }));
    };

    const handleRefresh = () => {
        if (!canRequest) {
            setError('Please select both start and end dates for the custom range.');
            return;
        }
        fetchLogs();
    };

    const handleDownload = () => {
        if (!token || !canRequest) return;
        axios
            .get(`/api/admin/system-logs/download?${buildQueryString()}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            })
            .then((res) => {
                const blob = new Blob([res.data], { type: 'text/plain' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const disposition = res.headers['content-disposition'];
                let filename = 'system-logs.log';
                if (disposition) {
                    const match = disposition.match(/filename="?([^";]+)"?/i);
                    if (match && match[1]) {
                        filename = match[1];
                    }
                }
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => {
                const message = err.response?.data?.msg || 'Failed to download system logs.';
                setError(message);
            });
    };

    const includedFiles = files.filter((file) => file.included);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                System Logs
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Inspect recent server activity, filter by time period, and download the relevant log snapshot for offline review.
            </Typography>

            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="system-log-period-label">Time Period</InputLabel>
                            <Select
                                labelId="system-log-period-label"
                                label="Time Period"
                                value={period}
                                onChange={handlePeriodChange}
                            >
                                {PERIOD_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {period === 'custom' && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label="Start"
                                    type="datetime-local"
                                    size="small"
                                    value={customRange.start}
                                    onChange={handleCustomChange('start')}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    label="End"
                                    type="datetime-local"
                                    size="small"
                                    value={customRange.end}
                                    onChange={handleCustomChange('end')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Stack>
                        )}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Tooltip title="Refresh log data">
                            <span>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    onClick={handleRefresh}
                                    disabled={!canRequest || loading}
                                >
                                    Refresh
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Download the current log selection">
                            <span>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                    disabled={!canRequest || loading || entries.length === 0}
                                >
                                    Download
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                            <Box flex={1}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Included Log Files
                                </Typography>
                                {includedFiles.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No log files matched the selected period.
                                    </Typography>
                                ) : (
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {includedFiles.map((file) => (
                                            <Chip
                                                key={file.name}
                                                label={`${file.name} • ${formatBytes(file.size)}`}
                                                variant="outlined"
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                            <Box flex={1}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Last Updated
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {lastUpdated ? formatTimestamp(lastUpdated) : 'Not fetched yet'}
                                </Typography>
                            </Box>
                        </Stack>

                        <Divider sx={{ mb: 2 }} />

                        {entries.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No log entries available for the selected period.
                            </Typography>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Timestamp</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Level</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>File</TableCell>
                                            <TableCell>Message</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {entries.map((entry) => (
                                            <TableRow key={`${entry.file}-${entry.lineNumber}-${entry.timestamp || entry.lineNumber}`}>
                                                <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                                    {formatTimestamp(entry.timestamp)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip {...levelToChipProps(entry.level)} />
                                                </TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                                    {entry.file}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                                    {entry.message || entry.raw}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        </Container>
    );
}

export default AdminSystemLogsPage;
